import { data_from_server_post } from '@/shared/http/client';
import { listActiveAiGenerationEntriesForUsecase } from '@/features/datasets/utils/datasetOverviewProgress';
import { listActiveRetrievalAgentEntriesForUsecase } from '@/features/datasets/utils/retrievalAgentProgress';
import { listActiveUploadEntriesForUsecase } from '@/features/datasets/utils/uploadProgress';
import {
    buildDistillationEventPayload,
    buildTrainingPayload,
    validateJudgeProviderConfigured,
    validateTeacherProviderConfigured,
} from './trainingUtils';

/**
 * Auto-train after data flows: arm/disarm API + in-flight flow discovery.
 *
 * Arming snapshots the exact `create_distillation_event` / `create_training`
 * payloads server-side (`usecase.auto_train_pending`); backend_consumer starts
 * the training when the last Generate-with-AI / Retrieval Agent / dataset or
 * benchmark upload flow of the task finishes.
 */

/** The trigger replaces this with the real id when it creates the event. */
export const AUTO_TRAIN_PLACEHOLDER_EVENT_ID = 'pending-auto-train';

export function isAutoTrainArmed(usecase) {
    return Boolean(usecase?.auto_train_pending);
}

/**
 * Should "Let's Train" stop at the data-flows gate modal?
 *
 * Gates while flows are still running, and also while auto-train is armed
 * with no visible flow left — that is the settle window ("Starting"/"Finalizing
 * data"): the flows finished but data_processor may still be inserting their
 * rows, so a bare Let's Train would silently train on a partial snapshot.
 *
 * @param {Array} activeFlows - result of `listActiveDataFlowsForUsecase`
 * @param {Object} usecase
 * @returns {{gate: boolean, flowCount: number, finalizing: boolean}} -
 *   `finalizing` marks the settle window (no running flow, pending armed).
 */
export function resolveDataFlowsGate(activeFlows, usecase) {
    const flowCount = activeFlows?.length || 0;
    const gate = flowCount > 0 || isAutoTrainArmed(usecase);
    return { gate, flowCount, finalizing: gate && flowCount === 0 };
}

/**
 * Map an `auto-train/arm` response to one UI action + toast.
 *
 * `triggering` (incl. `already_pending`) must never fall through to an
 * immediate manual training — the settle thread is about to start the
 * training itself, and a manual start would snapshot partial data.
 *
 * @param {Object} result - raw arm route response
 * @returns {{action: 'triggering'|'train_now'|'all_failed'|'scheduled'|'error',
 *   toast: {type: 'success'|'info'|'error', text: string}}}
 */
export function resolveArmResult(result) {
    if (result?.triggering || result?.already_pending) {
        return {
            action: 'triggering',
            toast: {
                type: 'success',
                text: 'Data is finalizing — training will start automatically',
            },
        };
    }
    if (result?.no_active_flows) {
        return {
            action: 'train_now',
            toast: {
                type: 'info',
                text: 'All data flows already finished — use "Let\'s Train" to start training now',
            },
        };
    }
    if (result?.all_failed) {
        return {
            action: 'all_failed',
            toast: {
                type: 'error',
                text: 'The data flows already failed — automatic training was not scheduled',
            },
        };
    }
    if (result?.armed) {
        return {
            action: 'scheduled',
            toast: {
                type: 'success',
                text: 'Training will start automatically when the data flows finish',
            },
        };
    }
    return {
        action: 'error',
        toast: {
            type: 'error',
            text: result?.detail || 'Failed to schedule automatic training',
        },
    };
}

/**
 * Active Generate-with-AI + Retrieval Agent + dataset/benchmark upload rows
 * for a task, tagged with `flowType` so callers can render mixed lists.
 */
export function listActiveDataFlowsForUsecase(progressStatus, usecaseId) {
    const generation = listActiveAiGenerationEntriesForUsecase(progressStatus, usecaseId).map(
        (entry) => ({ ...entry, flowType: 'generation' })
    );
    const retrieval = listActiveRetrievalAgentEntriesForUsecase(progressStatus, usecaseId).map(
        (entry) => ({ ...entry, flowType: 'retrieval' })
    );
    const uploads = listActiveUploadEntriesForUsecase(progressStatus, usecaseId).map(
        (entry) => ({ ...entry, flowType: 'upload' })
    );
    return [...generation, ...retrieval, ...uploads];
}

/**
 * Config-only pre-arm validation. Data-dependent checks (record counts,
 * label mismatches, unlabeled ratio) are skipped on purpose: the data is
 * still being generated/retrieved when the user arms auto-train.
 * @param {Object} [providerCatalog] - ``{ availableProviders, isLoading }`` from ``useCommercialModels()``
 */
export function validateAutoTrainConfig(usecase, providerCatalog) {
    if (!usecase?.prompt) {
        return {
            success: false,
            reason: 'missing_prompt',
            message: "You must go to 'Model Configuration' Step and fill the 'Prompt' field",
        };
    }
    const isTeacherRequired =
        usecase.comparison_mode && ['teacher', 'all'].includes(usecase.comparison_mode_option);
    if (isTeacherRequired && !usecase.teacher_model) {
        return {
            success: false,
            reason: 'missing_teacher_model',
            message: "You must go to 'Model Configuration' Step and fill the 'Commercial Model' field",
        };
    }
    if (isTeacherRequired && usecase.teacher_model) {
        const providerFailure = validateTeacherProviderConfigured(usecase, providerCatalog);
        if (providerFailure) {
            return providerFailure;
        }
    }
    const judgeFailure = validateJudgeProviderConfigured(usecase, providerCatalog);
    if (judgeFailure) {
        return judgeFailure;
    }
    if (isTeacherRequired && !usecase.parser_function) {
        return {
            success: false,
            reason: 'missing_parser_function',
            message: "You must go to 'Model Configuration' Step and fill the 'Response Parser' function",
        };
    }
    return { success: true };
}

/**
 * Snapshot the training payloads and arm auto-train on the backend.
 * `onSuccess` receives the raw route response
 * (`{ armed, auto_train_pending }`, `{ armed, triggering }`, or `{ no_active_flows }`).
 */
export function armAutoTrain({ usecase, trainingParams, onSuccess, onError }) {
    const { payload: distillationEventPayload, newVersion } = buildDistillationEventPayload(
        usecase,
        trainingParams
    );
    const trainingPayload = buildTrainingPayload(
        usecase,
        AUTO_TRAIN_PLACEHOLDER_EVENT_ID,
        newVersion,
        trainingParams
    );
    data_from_server_post(
        'auto-train/arm',
        {
            client_id: usecase?.client_id,
            usecase_id: usecase?._id,
            distillation_event_payload: distillationEventPayload,
            training_payload: trainingPayload,
        },
        (result) => onSuccess?.(result),
        (errorDetail) => onError?.(errorDetail || 'Failed to schedule automatic training')
    );
}

/** Cancel scheduled auto-training (running flows are untouched). */
export function disarmAutoTrain({ usecase, onSuccess, onError }) {
    data_from_server_post(
        'auto-train/disarm',
        {
            client_id: usecase?.client_id,
            usecase_id: usecase?._id,
        },
        (result) => {
            // The scheduled training was canceled, so the "Training will start
            // when data is ready" modal no longer counts as shown — a future
            // training start should surface the started modal normally.
            clearAutoTrainNotifyModalShown(usecase?._id);
            onSuccess?.(result);
        },
        (errorDetail) => onError?.(errorDetail || 'Failed to cancel automatic training')
    );
}

/**
 * Per-tab memory of "this session already saw the Notify Me modal for the
 * scheduled auto-train of this usecase" — set when arming shows the
 * "Training will start when data is ready" modal, consumed to suppress the
 * duplicate "Training Has Started!" modal when the auto-train later fires.
 * sessionStorage so it survives the wizard → task page navigation but stays
 * scoped to the tab that armed.
 */
const NOTIFY_MODAL_SHOWN_KEY_PREFIX = 'autoTrainNotifyModalShown:';

function getSessionStorage() {
    try {
        return globalThis.sessionStorage || null;
    } catch {
        return null;
    }
}

export function markAutoTrainNotifyModalShown(usecaseId) {
    if (!usecaseId) return;
    try {
        getSessionStorage()?.setItem(`${NOTIFY_MODAL_SHOWN_KEY_PREFIX}${usecaseId}`, '1');
    } catch {
        // Storage unavailable (private mode / quota) — worst case the user
        // sees the started modal again later.
    }
}

export function clearAutoTrainNotifyModalShown(usecaseId) {
    if (!usecaseId) return;
    try {
        getSessionStorage()?.removeItem(`${NOTIFY_MODAL_SHOWN_KEY_PREFIX}${usecaseId}`);
    } catch {
        // Nothing to clear when storage is unavailable.
    }
}

/** Returns whether the flag was set, clearing it (one consumer per arm). */
export function consumeAutoTrainNotifyModalShown(usecaseId) {
    if (!usecaseId) return false;
    try {
        const key = `${NOTIFY_MODAL_SHOWN_KEY_PREFIX}${usecaseId}`;
        const storage = getSessionStorage();
        const wasShown = storage?.getItem(key) === '1';
        if (wasShown) storage.removeItem(key);
        return wasShown;
    } catch {
        return false;
    }
}
